import React, { createContext, useContext, useState } from 'react';

import { Divider, ListItemIcon, Menu, MenuItem, MenuList, Paper, Popper } from '@mui/material';

export type BlockMenuItem = {
  id?: string;
  label: string;
  icon: React.ReactNode;
  run: () => void;
  divider?: boolean;
  danger?: boolean;
  disabled?: boolean;
  reask?: boolean;
};

type BlockMenuProps = {
  label: string;
  title: string;
  icon: React.ReactNode;
  items: Array<BlockMenuItem>;
  blockId?: string;
};

/** Id of the one block whose menu is forced open. Unset keeps normal behaviour. */
export const ForcedOpenMenuContext = createContext<string | undefined>(undefined);

export default function BlockMenu({ label, title, icon, items, blockId }: BlockMenuProps) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const forcedBlockId = useContext(ForcedOpenMenuContext);
  const [open, setOpen] = useState(false);
  const forced = blockId !== undefined && forcedBlockId === blockId;
  if (!items.length) return null;
  const menuItems = items.map((item) => [
    item.divider ? <Divider key={`${item.label}-divider`} /> : null,
    <MenuItem
      data-menu-item={item.id}
      dense
      disabled={item.disabled}
      key={item.label}
      onClick={() => {
        setOpen(false);
        item.run();
      }}
      sx={item.danger ? { color: 'error.main' } : undefined}
    >
      <ListItemIcon sx={item.danger ? { color: 'inherit' } : undefined}>{item.icon}</ListItemIcon>
      {item.label}
    </MenuItem>,
  ]);
  return (
    <>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        ref={setAnchor}
        title={title}
        type="button"
      >
        {icon}
      </button>
      {forced ? (
        <Popper
          anchorEl={anchor}
          disablePortal
          open
          placement="bottom-end"
          popperOptions={{ strategy: 'fixed' }}
          sx={{ zIndex: 30 }}
        >
          <Paper elevation={8} inert sx={{ pointerEvents: 'none' }}>
            <MenuList dense>{menuItems}</MenuList>
          </Paper>
        </Popper>
      ) : (
        <Menu
          anchorEl={anchor}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          onClick={(event) => event.stopPropagation()}
          onClose={() => setOpen(false)}
          open={open}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          {menuItems}
        </Menu>
      )}
    </>
  );
}
